import React from "react";
import { Link } from "react-router-dom";

/**
 * Exact missing-items checklist that gates public visibility. Rendered from
 * GET /api/business/publication-status — no guessing on the client.
 */
export default function ListingChecklist({ status }) {
  if (!status) return null;
  const bizMissing = status.business_missing || [];
  const svcMissing = (status.services || []).filter((s) => !s.published);
  const allGood = status.listed && svcMissing.length === 0 && status.published_service_count > 0;

  return (
    <div className="mt-6 rounded-2xl border p-6" data-testid="listing-checklist"
         style={{
           borderColor: allGood ? "var(--gf-border)" : "var(--gf-border-strong)",
           background: allGood ? "var(--gf-surface)" : "var(--gf-surface-2)",
         }}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-display text-xl" style={{ color: "var(--gf-text)" }}>
            {allGood ? "Your listing is live" : "Finish your listing to appear in search"}
          </h3>
          <p className="text-sm mt-1" style={{ color: "var(--gf-text-muted)" }}>
            {allGood
              ? `${status.published_service_count} ${status.published_service_count === 1 ? "service is" : "services are"} visible to customers.`
              : "Customers can't find you until every item below is done."}
          </p>
        </div>
        <span className="rounded-full px-3 py-1 text-[11px] uppercase tracking-[0.15em]"
              data-testid="listing-status-pill"
              style={allGood
                ? { background: "var(--gf-accent)", color: "var(--gf-on-accent)" }
                : { border: "1px solid var(--gf-border-strong)", color: "var(--gf-text-muted)" }}>
          {allGood ? "Listed" : "Not listed"}
        </span>
      </div>

      {!status.business_verified && (
        <div className="mt-4 text-sm" data-testid="checklist-subscription-item" style={{ color: "var(--gf-text)" }}>
          • Activate your subscription to publish services.{" "}
          <Link to="/business/subscribe" className="underline">Subscribe →</Link>
        </div>
      )}

      {bizMissing.length > 0 && (
        <ul className="mt-4 space-y-2" data-testid="checklist-business-missing">
          {bizMissing.map((m) => (
            <li key={m.code} className="text-sm flex gap-2" data-testid={`checklist-item-${m.code}`}>
              <span aria-hidden style={{ color: "var(--gf-text-muted)" }}>○</span>
              <span>
                <span style={{ color: "var(--gf-text)" }}>{m.label}</span>
                <span className="block text-xs" style={{ color: "var(--gf-text-muted)" }}>{m.detail}</span>
              </span>
            </li>
          ))}
        </ul>
      )}

      {svcMissing.length > 0 && (
        <div className="mt-5" data-testid="checklist-services-missing">
          <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>
            Services not yet visible
          </div>
          <ul className="mt-2 space-y-3">
            {svcMissing.map((s) => (
              <li key={s.id} className="text-sm" data-testid={`checklist-service-${s.id}`}>
                <span style={{ color: "var(--gf-text)" }}>{s.title}</span>
                <ul className="mt-1 ml-4 space-y-1">
                  {(s.missing || []).map((m) => (
                    <li key={m.code} className="text-xs" style={{ color: "var(--gf-text-muted)" }}>
                      — {m.label}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
